import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useJsApiLoader } from "@react-google-maps/api";

import CustomRefillForm from "./components/CustomRefillForm";
import DetailScaffold from "./components/DetailScaffold";
import LocationSheet from "./components/LocationSheet";
import ProductGridCard from "./components/ProductGridCard";
import SupplierDetailSheet from "./components/SupplierDetailSheet";
import SupplierInfoCard from "./components/SupplierInfoCard";
import TabStrip from "./components/TabStrip";
import { SupplierAvatar } from "./components/DetailBits";
// import { SpecList } from "./components/DetailBits";
import {
  AccessoryRailSkeleton,
  CustomRefillFormSkeleton,
  InfoCardSkeleton,
  ProductGridSkeleton,
  TabStripSkeleton,
} from "./components/DetailSkeletons";
import SmartImage from "../../components/SmartImage";

import useDetailOrigin from "./useDetailOrigin";
import useSupplierDetail from "./useSupplierDetail";
import { useSupplierRating } from "./useSupplierRatings";
import useCustomRefill from "./useCustomRefill";
import {
  useFillTypes,
  useSupplierAccessories,
  useSupplierProducts,
} from "./useSupplierBody";
import { supplierProductPath, CART_PATH } from "./detailRoutes";

import useAddToCart from "./useAddToCart";
import useCart from "../cart/useCart";
import useSupplierSave from "../favourites/useSupplierSave";
import { getAuthToken } from "../../lib/api/tokenStore";
import { GOOGLE_MAP_KEY } from "../../utils/configs";

import "./styles.css";

const MAP_LIBRARIES = ["places"];
const HERO_HEIGHT = 400;

const SupplierProfileRoute = () => {
  const navigate = useNavigate();
  const { supplierId: supplierIdParam } = useParams();
  const supplierId = Number(supplierIdParam);
  const hasValidId = Number.isFinite(supplierId) && supplierId > 0;

  const origin = useDetailOrigin();

  const { containerOptions } = useSelector((state) => state.publicAPIs);

  const { isLoaded } = useJsApiLoader({
    id: "supplier-google-map",
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries: MAP_LIBRARIES,
  });

  const { detail, distanceKm, notice, isLoading, isError, refetch } =
    useSupplierDetail({
      supplierId,
      routeDistanceKm: origin.distanceKm,
      coordinates: origin.coordinates,
    });

  const tabs = useFillTypes({
    containerOptions,
    supportedIds: detail?.supportedContainerOptionIds,
  });

  const [activeTabId, setActiveTabId] = useState(null);
  const [ratingsOpen, setRatingsOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);

  useEffect(() => {
    if (!tabs.length) return;

    setActiveTabId((current) =>
      current && tabs.some((tab) => tab.id === current) ? current : tabs[0].id
    );
  }, [tabs]);

  const activeTab = useMemo(
    () => tabs.find((tab) => tab.id === activeTabId) || null,
    [tabs, activeTabId]
  );

  const rating = useSupplierRating({ supplierId });
  const save = useSupplierSave({ enabled: Boolean(getAuthToken()) });

  const isAccessoryTab = Boolean(activeTab?.accessory);
  const isRefillTab = Boolean(activeTab?.variableVolume);

  const grid = useSupplierProducts({
    supplierId,
    containerOptionId: activeTab?.id,
    enabled: Boolean(activeTab) && !isAccessoryTab && !isRefillTab,
  });

  const accessories = useSupplierAccessories({
    supplierId,
    enabled: isAccessoryTab,
  });

  const customRefill = useCustomRefill({
    supplierId,
    containerOptionId: activeTab?.id,
    enabled: isRefillTab,
  });

  const cart = useCart({ enabled: Boolean(getAuthToken()) });
  const { busyAction, addToCart, conflictDialog } = useAddToCart({ cart });

  const submitCustomRefill = useCallback(
    async (action) => {
      if (!customRefill.canSubmit) return;

      const result = await addToCart({
        action,
        notice,
        supplierName: detail?.name,
        items: customRefill.cartEntries,
      });

      if (result?.status === "added" && action === "buy") navigate(CART_PATH);
    },
    [customRefill.canSubmit, customRefill.cartEntries, addToCart, notice, detail, navigate]
  );

  const openProduct = useCallback(
    (product) =>
      navigate(
        supplierProductPath({
          supplierId,
          productId: product.id,
          coordinates: origin.coordinates,
        }),
        { state: { notice, supplierName: detail?.name, supplierLogo: detail?.logoUrl } }
      ),
    [navigate, supplierId, origin.coordinates, notice, detail]
  );

  const savedSupplier = detail
    ? { id: detail.id, name: detail.name, saved: detail.saved }
    : null;

//   const storeFacts = useMemo(() => {
//     if (!detail) return [];

//     return [
//       { label: "Status", value: detail.online ? "Open now" : "Currently closed" },
//       {
//         label: "Delivers Within",
//         value:
//           Number.isFinite(detail.deliveryRadiusKm) && detail.deliveryRadiusKm > 0
//             ? `${Math.round(detail.deliveryRadiusKm)} km`
//             : "",
//       },
//       {
//         label: "Average Price",
//         value: Number.isFinite(detail.pricePerKg)
//           ? `${formatMoney(detail.pricePerKg, detail.currency)} per Kg`
//           : "",
//       },
//       {
//         label: "Starting From",
//         value: Number.isFinite(detail.priceFrom)
//           ? formatMoney(detail.priceFrom, detail.priceFromCurrency)
//           : "",
//       },
//       { label: "Address", value: detail.addressLine },
//     ].filter((row) => row.value);
//   }, [detail]);

  if (isError || !hasValidId) {
    return (
      <div className="sy-detail-error">
        <p>We could not load this supplier.</p>

        <button type="button" className="sy-btn sy-btn-primary" onClick={refetch}>
          Try again
        </button>
      </div>
    );
  }

  const showFrame = isLoading || !tabs.length;

  return (
    <DetailScaffold
      title={detail?.name || "Supplier"}
      showTitle={false}
      heroHeight={HERO_HEIGHT}
      triggerOffset={90}
      layout="split"
      heroAuto
      hero={
        <div className="sy-profile-side">
          {detail?.bannerUrl ? (
            <SmartImage
              className="sy-profile-banner"
              src={detail.bannerUrl}
              alt=""
              priority
            />
          ) : (
            <div className="sy-profile-banner sy-profile-banner-empty" aria-hidden="true">
              <SupplierAvatar name={detail?.name} logoUrl={detail?.logoUrl} size={72} />
            </div>
          )}
        </div>
      }
      heroFoot={
        detail && (
          <section className="sy-profile-about">
            <h2>About {detail.name}</h2>

            <p className={detail.about ? "" : "sy-muted"}>
              {detail.about || "This supplier has not added a description yet."}
            </p>
          </section>
        )
      }
      full={
        showFrame ? (
          <>
            <TabStripSkeleton />
            <ProductGridSkeleton />
          </>
        ) : (
          <>
            <section className="sy-product-info">
              <h2>Available products</h2>

              <TabStrip tabs={tabs} activeId={activeTabId} onSelect={setActiveTabId} />

              {isRefillTab ? (
                customRefill.isLoading ? (
                  <CustomRefillFormSkeleton />
                ) : customRefill.hasCylinders ? (
                  <>
                    <CustomRefillForm
                      entries={customRefill.entries}
                      cylinderOptions={customRefill.cylinderOptions}
                      materialOptionsOf={customRefill.materialOptionsOf}
                      refillVolumeOptionsOf={customRefill.refillVolumeOptionsOf}
                      total={customRefill.total}
                      onSelectCylinder={customRefill.setCylinder}
                      onSelectMaterial={customRefill.setMaterial}
                      onSelectRefillType={customRefill.setRefillType}
                      onSelectRefillVolume={customRefill.setRefillVolume}
                      onAddEntry={customRefill.addEntry}
                      onRemoveEntry={customRefill.removeEntry}
                    />

                    <div className="sy-refill-actions">
                      <button
                        type="button"
                        className="sy-btn sy-btn-outline"
                        disabled={!customRefill.canSubmit || Boolean(busyAction)}
                        onClick={() => submitCustomRefill("add")}
                      >
                        {busyAction === "add" ? "Adding…" : "Add to Cart"}
                      </button>

                      <button
                        type="button"
                        className="sy-btn sy-btn-primary"
                        disabled={!customRefill.canSubmit || Boolean(busyAction)}
                        onClick={() => submitCustomRefill("buy")}
                      >
                        {busyAction === "buy" ? "Working…" : "Buy Now"}
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="sy-empty">
                    This supplier has no {activeTab.label.toLowerCase()} cylinders listed.
                  </p>
                )
              ) : isAccessoryTab ? (
                accessories.isLoading ? (
                  <AccessoryRailSkeleton />
                ) : accessories.sections.length ? (
                  accessories.sections.map((section) => (
                    <section className="sy-rail-section" key={section.materialCode}>
                      <h3>{section.title}</h3>

                      <ul className="sy-product-grid">
                        {section.items.map((product) => (
                          <ProductGridCard
                            key={product.id}
                            product={product}
                            onOpen={openProduct}
                          />
                        ))}
                      </ul>
                    </section>
                  ))
                ) : (
                  <p className="sy-empty">This supplier has no accessories listed.</p>
                )
              ) : grid.isLoading ? (
                <ProductGridSkeleton />
              ) : grid.products.length ? (
                <ul className="sy-product-grid">
                  {grid.products.map((product) => (
                    <ProductGridCard
                      key={product.id}
                      product={product}
                      onOpen={openProduct}
                    />
                  ))}
                </ul>
              ) : (
                <p className="sy-empty">
                  No {activeTab?.label} available from this supplier.
                </p>
              )}
            </section>

            {/*
            {storeFacts.length > 0 && (
              <section className="sy-product-info sy-store-details">
                <h2>Store details</h2>

                <SpecList items={storeFacts} />
              </section>
            )}
            */}
          </>
        )
      }
    >
      {showFrame ? (
        <InfoCardSkeleton />
      ) : (
        <SupplierInfoCard
          name={detail.name}
          logoUrl={detail.logoUrl}
          rating={rating.average ?? detail.rating}
          totalRatings={rating.total ?? detail.totalRatings}
          ratingLoading={rating.isLoading}
          distanceKm={distanceKm}
          online={detail.online}
          deliveryRadiusKm={detail.deliveryRadiusKm}
          priceFrom={detail.priceFrom}
          priceFromCurrency={detail.priceFromCurrency}
          addressLine={detail.addressLine}
          coordinates={detail.coordinates}
          origin={origin.coordinates}
          mapsReady={isLoaded}
          notice={notice}
          saved={savedSupplier ? save.isSaved(savedSupplier) : false}
          savePending={savedSupplier ? save.isPending(savedSupplier) : false}
          canSave={save.canSave}
          onToggleSave={() => savedSupplier && save.toggleSaved(savedSupplier)}
          onOpenRatings={() => setRatingsOpen(true)}
          onOpenLocation={() => setLocationOpen(true)}
        />
      )}

      <SupplierDetailSheet
        open={ratingsOpen}
        supplierId={supplierId}
        supplierName={detail?.name}
        initialTab="reviews"
        onClose={() => setRatingsOpen(false)}
      />

      <LocationSheet
        open={locationOpen}
        supplierName={detail?.name || "Supplier"}
        supplier={detail?.coordinates}
        origin={origin.coordinates}
        mapsReady={isLoaded}
        onClose={() => setLocationOpen(false)}
      />

      {conflictDialog}
    </DetailScaffold>
  );
};

export default SupplierProfileRoute;
